import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { NotificationService, Notification } from '../../../core/services/notification.service';
import { RealtimeService, Incident } from '../../../core/services/realtime.service';
import { PinKeypadComponent } from '../common-modal/pin-keypad.component';
import { SettingsService } from '../../../core/services/settings.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ResponseModalComponent } from '../response-modal/response-modal.component';
import { ArcAngelMonitorModeService } from '../arc-angel-monitor-mode.service';
import { IncidentCategoryService } from '../../../core/services/incident-category.service';
import { SessionOptionsService } from '../../../core/services/session-option.service';
import { ArcAngelLiveModeService } from '../../../core/services/arc-angel-live-mode.service';
import { CameraService } from '../../../core/services/camera.service';
import { FloorService } from '../../../core/services/floor.service';
interface ParsedNotification {
  name: string;
  description?: string;
  status?: string;
  type?: string;
  created?: string;
  updated?: string;
}
@Component({
  selector: 'app-notification',
  standalone: true,
  imports: [CommonModule, PinKeypadComponent],
  templateUrl: './notification.component.html',
  styleUrls: ['./notification.component.css'],
})

export class NotificationComponent {
  @Input() showNotifications = false;
  @Input() autoOpenOnNewNotifications = false;
  @Output() showNotificationsChange = new EventEmitter<boolean>();
  @Output() openPinDialog = new EventEmitter<any>();
  @Input() geofenceResults: any;
  ModalId: any;
  showPinDialog: boolean = false;
  // Component state
  notifications: Notification[] = [];
  latestNotification: Notification | null = null;

  notificationCount = 0;
  connectionStatus: 'connected' | 'disconnected' | 'connecting' = 'disconnected';
  info: any = {};
  pinValue: string = '';
  showBuildingCamera: boolean = false;
  // Subscriptions
  private notificationsSubscription!: Subscription;
  private latestNotificationSubscription!: Subscription;
  private realtimeSubscriptions: Subscription[] = [];
  private previousNotificationCount = 0;
  private isIncidentVisualizerOpen = false;
  private visualizerVisibilitySubscription?: Subscription;


  constructor(
    private sessionOptionsService: SessionOptionsService,
    private notificationService: NotificationService,
    private realtimeService: RealtimeService,
    private settingService: SettingsService,
    private modalService: NgbModal,
    private arcAngelMonitorModeService: ArcAngelMonitorModeService,
    private incidentCategoryService: IncidentCategoryService,
    private arcAngelLiveModeService: ArcAngelLiveModeService,
    private camera: CameraService,
    private floorService: FloorService,
  ) { }

  ngOnInit(): void {
    this.initializeSubscriptions();
    this.initializeRealTimeConnection();
    this.notifications = this.sortNotificationsByCreated(this.notifications);
    this.subscribeToVisualizerVisibility();
  }

  private subscribeToVisualizerVisibility(): void {
    // Subscribe to visualizer visibility to block notification clicks when visualizer is open
    this.visualizerVisibilitySubscription = this.floorService.incidentVisualizerVisibility$.subscribe(
      (isOpen: boolean) => {
        this.isIncidentVisualizerOpen = isOpen;
      }
    );
  }

  ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.realtimeService.disconnect();
    if (this.visualizerVisibilitySubscription) {
      this.visualizerVisibilitySubscription.unsubscribe();
    }
  }

  private initializeSubscriptions(): void {
    // Subscribe to notifications list
    this.notificationsSubscription = this.notificationService.notifications$.subscribe(
      (notifications: Notification[]) => {
        // Merge latestNotification if not present
        let mergedNotifications = [...notifications];
        if (
          this.latestNotification &&
          this.latestNotification.isVisible &&
          !mergedNotifications.some(n => n.id === this.latestNotification!.id)
        ) {
          mergedNotifications = [this.latestNotification, ...mergedNotifications];
        }
        
        // Sort notifications by created date (descending - newest first)
        this.notifications = this.sortNotificationsByCreatedDate(mergedNotifications);
        this.notificationCount = this.notifications.length;
        this.syncNotificationPanelState();
      }
    );

    // Subscribe to latest notification (toast)
    this.latestNotificationSubscription = this.notificationService.latestNotification$.subscribe(
      (notification: Notification | null) => {
        this.latestNotification = notification;
        // Optionally trigger a re-merge if needed
        if (notification && notification.isVisible) {
          if (!this.notifications.some(n => n.id === notification.id)) {
            this.notifications = [notification, ...this.notifications];
            // Re-sort after adding new notification
            this.notifications = this.sortNotificationsByCreatedDate(this.notifications);
            this.notificationCount = this.notifications.length;
          }
        }
        this.syncNotificationPanelState();
      }
    );
  }

  private syncNotificationPanelState(): void {
    const currentCount = this.notifications.length;

    if (currentCount === 0) {
      this.closeNotifications();
    } else if (
      this.autoOpenOnNewNotifications &&
      !this.showNotifications &&
      currentCount > this.previousNotificationCount
    ) {
      this.showNotificationsPanel();
    }

    this.previousNotificationCount = currentCount;
  }
  
  public getNotificationColor(type: string): string {
    switch (type) {
      case 'ACTIVE': return 'rgba(239, 68, 68, 0.9)';
      case 'PENDING': return 'rgba(245, 158, 11, 0.9)';
      case 'SUCCESS': return 'rgba(16, 185, 129, 0.9)';
      default: return 'rgba(59, 130, 246, 0.9)';
    }
  }


  private initializeRealTimeConnection(): void {
    // Connect to real-time service
    this.realtimeService.connect();

    // Subscribe to connection status
    const connectionSub = this.realtimeService.connectionStatus.subscribe(
      status => {
        this.connectionStatus = status;
        this.handleConnectionStatusChange(status);
      }
    );
    this.realtimeSubscriptions.push(connectionSub);

    // Subscribe to all incidents
    const incidentsSub = this.realtimeService.allIncidents.subscribe(
      incidents => {
      }
    );
    this.realtimeSubscriptions.push(incidentsSub);

    // Subscribe to new incidents
    const newIncidentsSub = this.realtimeService.incidents.subscribe(
      incident => {
      }
    );
    this.realtimeSubscriptions.push(newIncidentsSub);
  }

  private handleConnectionStatusChange(status: 'connected' | 'disconnected' | 'connecting'): void {
    switch (status) {
      case 'connected':
        break;
      case 'connecting':
        break;
      case 'disconnected':
        break;
    }
  }

  private cleanupSubscriptions(): void {
    if (this.notificationsSubscription) {
      this.notificationsSubscription.unsubscribe();
    }
    if (this.latestNotificationSubscription) {
      this.latestNotificationSubscription.unsubscribe();
    }
    
    // Cleanup real-time subscriptions
    this.realtimeSubscriptions.forEach(sub => sub.unsubscribe());
    this.realtimeSubscriptions = [];
  }

  // Toggle notifications dropdown
  toggleNotifications(): void {
    this.showNotifications = !this.showNotifications;
    this.showNotificationsChange.emit(this.showNotifications); // Emit boolean
  }

  // Close notifications dropdown
  closeNotifications(): void {
    this.showNotifications = false;
    this.showNotificationsChange.emit(false); // Emit boolean
  }

  showNotificationsPanel(): void {
    this.showNotifications = true;
    this.showNotificationsChange.emit(true); // Emit boolean
  }

  // Clear all notifications
  clearAllNotifications(): void {
    this.notificationService.clearAll(); // Call the service method, not itself
    this.showNotifications = false; // Close dropdown after clearing
  }

  // Handle notification click
  onNotificationClick(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId) || this.latestNotification;
    if (!notification) return;
  
    // Check if incident visualizer is open - prevent navigation to other incidents
    if (this.isIncidentVisualizerOpen) {
      return; // Exit early - no navigation will happen until visualizer is closed
    }
  
    // Check if building camera is already open - prevent navigation to other incidents
    if (this.camera.isBuildingCameraOpen()) {
      return; // Exit early - no navigation will happen
    }
  
    // Try extract incident ID from notification
    const incidentId = this.extractIncidentId(notificationId);
  
    if (incidentId) {
      this.navigateToIncident(incidentId);
      return;
    }
  
    // Fallback: find incident from content
    const derivedId = this.findIncidentIdFromContent(notification);
    if (derivedId) {
      this.navigateToIncident(derivedId);
      return;
    }
  
    // Final fallback: custom handler
    const extended: any = notification;
    if (typeof extended.onGoLive === 'function') {
      extended.onGoLive();
    }
  }
  
  /** Core navigation – now only decides mode */
  private navigateToIncident(incidentId: string): void {
    if (!incidentId) return;
  
    // Set organization context
    const orgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(incidentId);
    if (orgId) {
      this.incidentCategoryService.setManagingOrgId(orgId);
    }
  
    const incidents = this.realtimeService.getCurrentIncidents();
    const incident = incidents.find(i => i.id === incidentId);
    const isLive = incident && ['ACTIVE', 'active'].includes(incident.status);
    
    // Check if incident is non-building by checking existing monitor mode entities
    // Non-building incidents should always open in Monitor Mode, not Live Mode
    const monitorIncidentEntity = this.arcAngelMonitorModeService.getMonitorEvents().find(
      (event: any) => event.eventId === incidentId || event.id === incidentId
    );
    const isNonBuilding = monitorIncidentEntity && 
      typeof monitorIncidentEntity.isBuildingIncident === 'function' && 
      !monitorIncidentEntity.isBuildingIncident();
  
    // If non-building incident, always use Monitor Mode (even if ACTIVE)
    // If building incident and ACTIVE, use Live Mode
    // Otherwise use Monitor Mode
    if (isNonBuilding && incident?.status !== 'PENDING') {
      // Non-building incidents always open in Monitor Mode with visualizer
      this.handleNonBuildingIncidentNavigation(incidentId);
    } else if (isLive) {
      // Building incidents that are ACTIVE open in Live Mode
      this.handleLiveModeNavigation(incidentId);
    } else {
      // All other incidents open in Monitor Mode
      this.handleMonitorModeNavigation(incidentId);
    }
  }

  /* -----------------------------------------------------
   * NON-BUILDING INCIDENT MODE HANDLER (Monitor Mode with Visualizer)
   * ----------------------------------------------------- */
  private handleNonBuildingIncidentNavigation(incidentId: string): void {
    // Reset flag for monitor mode (we want to show event details in monitor mode)
    this.camera.setHideEventDetailsForLiveNotification(false);
    // Ensure we are in Monitor mode
    if (!this.sessionOptionsService.isMonitorMode()) {
      this.sessionOptionsService.goToMonitorMode();
    }
    
    this.camera.setCurrentIncidentId(incidentId);
    
    // Open in Monitor Mode with location navigation and visualizer
    this.arcAngelMonitorModeService
      .startMonitorIncidentMode(incidentId, { sessionOptionsService: this.sessionOptionsService })
      .then(() => {
        // After monitor mode starts, handle location navigation and visualizer (same as live mode)
        setTimeout(() => {
          const incidentEntity = this.arcAngelMonitorModeService.getMonitorEvents().find(
            (event: any) => event.eventId === incidentId || event.id === incidentId
          );
          
          if (incidentEntity) {
            // Navigate to location if available (same as live mode)
            if (incidentEntity.locations) {
              try {
                // Use live mode service methods for location navigation
                const rectangle = this.arcAngelLiveModeService['getRectangleFromLocations'](incidentEntity.locations);
                this.arcAngelLiveModeService['goToAdHocRegion'](rectangle);
              } catch (error) {
                console.error('[Notification] Error navigating to incident location in monitor mode:', error);
              }
            }
            // Open visualizer in monitor mode (visualizer component will adapt to monitor mode)
            this.arcAngelLiveModeService['openIncidentVisualizer'](incidentEntity, incidentEntity);
          }
        }, 100);
      })
      .catch((error: unknown) => {
        console.error(`Failed to navigate to non-building incident ${incidentId} in monitor mode`, error);
        this.notificationService.addDangerNotification(
          `Unable to open incident. Please try again.`,
          { moveToTop: true, duration: 4000 }
        );
      });
  }
  
  /* -----------------------------------------------------
   * LIVE INCIDENT MODE HANDLER
   * ----------------------------------------------------- */
  private handleLiveModeNavigation(incidentId: string): void {
    // Set flag to show Create Incident screen when opening from notification in monitor mode
    this.camera.setHideEventDetailsForLiveNotification(true);
    // Ensure we are in Monitor mode
    if (!this.sessionOptionsService.isMonitorMode()) {
      this.sessionOptionsService.goToMonitorMode();
    }

    this.camera.setCurrentIncidentId(incidentId);

    this.arcAngelMonitorModeService
      .startMonitorIncidentMode(incidentId, { sessionOptionsService: this.sessionOptionsService })
      .catch((error: unknown) => {
        console.error(`Failed to navigate to incident ${incidentId} in monitor mode`, error);
        this.notificationService.addDangerNotification(
          `Unable to open incident. Please try again.`,
          { moveToTop: true, duration: 4000 }
        );
      });
  }
  
  /* -----------------------------------------------------
   * MONITOR INCIDENT MODE HANDLER
   * ----------------------------------------------------- */
  private handleMonitorModeNavigation(incidentId: string): void {
    // Reset flag for monitor mode (we want to show event details in monitor mode)
    this.camera.setHideEventDetailsForLiveNotification(false);
    // Ensure we are in Monitor mode
    if (!this.sessionOptionsService.isMonitorMode()) {
      this.sessionOptionsService.goToMonitorMode();
    }
  
    this.camera.setCurrentIncidentId(incidentId);
  
    this.arcAngelMonitorModeService
      .startMonitorIncidentMode(incidentId, { sessionOptionsService: this.sessionOptionsService })
      .catch((error: unknown) => {
        console.error(`Failed to navigate to incident ${incidentId} in monitor mode`, error);
        this.notificationService.addDangerNotification(
          `Unable to open incident. Please try again.`,
          { moveToTop: true, duration: 4000 }
        );
      });
  }

  private extractIncidentId(notificationId: string): string | null {
    if (!notificationId) return null;
  
    // List of known patterns
    const patterns = [
      /^incident-(.+)$/i,
      /^specific-incident-(.+)$/i,
      /^incident-update-(.+)$/i,
      /^api-incident-(.+)$/i
    ];
  
    // Check pattern-based matches
    for (const pattern of patterns) {
      const match = notificationId.match(pattern);
      if (match?.[1]) {
        return match[1];
      }
    }
  
    // Direct match against current incidents (raw incidentId as notificationId)
    const incidents = this.realtimeService.getCurrentIncidents();
    const directMatch = incidents.find(i => i.id === notificationId);
  
    return directMatch ? notificationId : null;
  }

  private findIncidentIdFromContent(notification: any): string | null {
    if (!notification || !notification.content) return null;
  
    const content = notification.content;
  
    // Parse content (your existing parser)
    const info = this.parseNotificationContent(content);
  
    // Get all active incidents
    const incidents = this.realtimeService.getCurrentIncidents();
    if (!incidents || incidents.length === 0) return null;
  
    // 1. Match by incident name
    const byName = incidents.find(inc => inc.name === info.name);
    if (byName) return byName.id;
  
    // 2. Match by incident ID being inside the content text
    const byId = incidents.find(inc => content.includes(inc.id));
    if (byId) return byId.id;
  
    return null;
  }  


  // Dismiss specific notification
  dismissNotification(notificationId: string): void {
    this.notificationService.deleteNotification(notificationId);
  }

  // Go live to event
  goLiveToEvent(notificationId: string): void {
    const notification = this.notifications.find(n => n.id === notificationId) || this.latestNotification;
    if (notification) {
      const extendedNotification = notification as any;
      if (extendedNotification.onGoLive && typeof extendedNotification.onGoLive === 'function') {
        extendedNotification.onGoLive();
      }
    }
  }

  // Close latest notification toast
  closeLatestNotification(): void {
    if (this.latestNotification) {
      this.notificationService.deleteNotification(this.latestNotification.id);
    }
  }

  // Format timestamp for display
  formatTime(timestamp: Date): string {
    const now = new Date();
    const diff = now.getTime() - new Date(timestamp).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return new Date(timestamp).toLocaleDateString();
  }

  // Track by function for ngFor optimization
  trackByNotificationId(index: number, notification: Notification): string {
    return notification.id;
  }

  // Get connection status indicator class
  getConnectionStatusClass(): string {
    return `status-${this.connectionStatus}`;
  }

  // Check if connection is healthy
  isConnected(): boolean {
    return this.connectionStatus === 'connected';
  }

  // Manual reconnect
  reconnect(): void {
    this.realtimeService.disconnect();
    setTimeout(() => {
      this.realtimeService.connect();
    }, 1000);
  }

  // Add method to handle API incident data
  private handleApiIncidentData(incidents: Incident[]): void {
    
    // Clear existing incident notifications
    incidents.forEach(incident => {
      this.notificationService.deleteNotification(`incident-${incident.id}`);
    });

    // Create notifications for active incidents
    const activeIncidents = incidents.filter(incident => incident.status === 'ACTIVE');
    
    activeIncidents.forEach(incident => {
      const content = incident.name;
      const description = incident.description || 'No description available';
      
      this.notificationService.addDangerNotification(
        `${content}\n${description}`,
        {
          id: `api-incident-${incident.id}`,
          moveToTop: false, // Don't show as popup unless it's new
          onGoLive: () => {
            
          }
        }
      );
    });
  }

  // Method to refresh notifications from API
  refreshFromApi(): void {
    this.realtimeService.refresh();
  }

  // Get current incidents
  getCurrentIncidents(): Incident[] {
    return this.realtimeService.getCurrentIncidents();
  }

  // Test method to manually trigger incident notifications
  testIncidentNotifications(): void {
    const currentIncidents = this.getCurrentIncidents();
    
    if (currentIncidents.length > 0) {
      this.handleApiIncidentData(currentIncidents);
    } else {
      this.notificationService.addInfoNotification('No incidents found. Refreshing...', {
        moveToTop: true,
        duration: 3000
      });
      this.refreshFromApi();
    }
  }

  // Utility methods for testing (optional - can be removed in production)
  addTestNotifications(): void {
    // Test different notification types
    this.notificationService.addSuccessNotification('User saved successfully!');
    
    this.notificationService.addWarningNotification('System maintenance scheduled', {
      id: 'maintenance-warning',
      moveToTop: true
    });
    
    this.notificationService.addDangerNotification('Critical error occurred', {
      onGoLive: () => {
      
      }
    });
    
    this.notificationService.addInfoNotification('New message received', {
      onClick: () => {
      },
      moveToTop: true
    });
  }

  // Handle click outside dropdown to close it
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    const notificationContainer = target.closest('.notification-container');
    
    if (!notificationContainer && this.showNotifications) {
      this.closeNotifications();
    }
  }

  // Keyboard accessibility
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.showNotifications) {
      this.closeNotifications();
    }
  }

  // Get notification type class for styling
  getNotificationTypeClass(type: string): string {
    return `notification-${type}`;
  }

  // Check if notification is recent (less than 5 minutes)
  isRecentNotification(timestamp: Date): boolean {
    const fiveMinutesAgo = new Date(Date.now() - 5 * 60 * 1000);
    return timestamp > fiveMinutesAgo;
  }

  // Get unread notifications count
  getUnreadCount(): number {
    return this.notifications.filter(n => n.isVisible).length;
  }

  // Mark all notifications as read
  markAllAsRead(): void {
    this.notifications.forEach(notification => {
      notification.isVisible = false;
    });
    this.notificationCount = 0;
  }

  // Get notifications by type
  getNotificationsByType(type: 'info' | 'success' | 'warning' | 'danger'): Notification[] {
    return this.notifications.filter(n => n.type === type);
  }

  // Check if there are any critical notifications
  hasCriticalNotifications(): boolean {
    return this.notifications.some(n => n.type === 'danger');
  }

  // Get most recent notification
  getMostRecentNotification(): Notification | null {
    if (this.notifications.length === 0) return null;
    
    return this.notifications.reduce((most, current) => 
      current.timestamp > most.timestamp ? current : most
    );
  }

  // Check if notification should show as toast
  shouldShowAsToast(notification: Notification): boolean {
    const extendedNotification = notification as any;
    return extendedNotification.moveToTop === true;
  }

  // Check if notification has Go Live action
  hasGoLiveAction(notification: Notification): boolean {
    const extendedNotification = notification as any;
    return typeof extendedNotification.onGoLive === 'function';
  }

  // Extract alert title from notification content
  getAlertTitle(notification: Notification): string {
    const content = notification.content;
    this.info = this.parseNotificationContent(content);

    // Return the name/title as a string
    return this.info.name || 'Untitled Alert';
  }

  // Add separate methods to get other properties
  getAlertName(notification: Notification): string {
    const info = this.parseNotificationContent(notification.content);
    return info.name || 'Untitled Alert';
  }

  getAlertDescriptionParsed(notification: Notification): string {
    const info = this.parseNotificationContent(notification.content);
    return info.description || 'No description available';
  }

  getAlertCreated(notification: Notification): string {
    const info = this.parseNotificationContent(notification.content);   
    return info.Created || 'Not specified';
  }

  getAlertUpdated(notification: Notification): string {
    const info = this.parseNotificationContent(notification.content);
    return info.updated || 'Not specified';
  }

//   parseNotificationContent(content: string): any {
   
//   const lines = content.split('\n');
//   const info: any = {};
//   lines.forEach(line => {
//     if (line.includes(':')) {
//       const [key, value] = line.split(':');
//       const cleanKey = key.trim().toLowerCase();
//       if (cleanKey.includes('incident') || cleanKey.includes('new')) {
//         info.name = value.trim();
//       } else if (cleanKey.includes('description')) {
//         info.description = value.trim();
//       }
//       else if (cleanKey.includes('created')) {
//         info.Created = value;
       
//       }
//     }
//   });
//   if (!info.name && lines.length > 0) info.name = lines[0];
//   if (!info.description && lines.length > 1) info.description = lines[1];
//   if (!info.Created && lines.length > 1) info.Created = lines[4];
//   return info;
// }
parseNotificationContent(content: string): any {
  const lines = content.split('\n');
  const info: any = {};

  lines.forEach(line => {
    const colonIndex = line.indexOf(':');
    if (colonIndex !== -1) {
      const key = line.substring(0, colonIndex).trim().toLowerCase();
      const value = line.substring(colonIndex + 1).trim();

      if (key.includes('incident') || key.includes('new')) {
        info.name = value;
      } else if (key.includes('description')) {
        info.description = value;
      } else if (key.includes('created')) {
        const createdMatch = value.match(/(.+?)\sGMT/);
        info.Created = createdMatch ? createdMatch[1].trim() : value;
      }
    }
  });

  // Fallbacks
  if (!info.name && lines.length > 0) info.name = lines[0].trim();
  if (!info.description && lines.length > 1) info.description = lines[1].trim();
  if (!info.Created && lines.length > 4) {
    const fallbackCreated = lines[4];
    const colonIndex = fallbackCreated.indexOf(':');
    const value = fallbackCreated.substring(colonIndex + 1).trim();
    const createdMatch = value.match(/(.+?)\sGMT/);
    info.Created = createdMatch ? createdMatch[1].trim() : value;
  }

  return info;
}

  // Format timestamp like your image (Thursday Aug 07 12:35 pm)
  // formatFullTimestamp(timestamp: Date): string {
  //   const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  //   const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
  //   const dayName = days[timestamp.getDay()];
  //   const monthName = months[timestamp.getMonth()];
  //   const day = timestamp.getDate().toString().padStart(2, '0');
    
  //   // Format time as 12-hour with am/pm
  //   let hours = timestamp.getHours();
  //   const minutes = timestamp.getMinutes().toString().padStart(2, '0');
  //   const ampm = hours >= 12 ? 'pm' : 'am';
  //   hours = hours % 12;
  //   hours = hours ? hours : 12; // 0 should be 12
    
  //   return `${dayName} ${monthName} ${day} ${hours}:${minutes} ${ampm}`;
  // }
  formatFullTimestamp(timestamp: Date | string): string {
  const date = new Date(timestamp);
  if (isNaN(date.getTime())) return 'Invalid date';

  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  const dayName = days[date.getDay()];
  const monthName = months[date.getMonth()];
  const day = date.getDate().toString().padStart(2, '0');

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;

  return `${dayName} ${monthName} ${day} ${hours}:${minutes} ${ampm}`;
}


  // Get alert card CSS class based on type (all red as requested)
  getAlertCardClass(notification: Notification): string {
    // Extract status from notification content or use a status property
    const status = this.getNotificationStatus(notification);
    
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'alert-danger';     // RED for Active
      case 'PENDING':
        return 'alert-warning';    // ORANGE for Pending  
      case 'RESOLVED':
      case 'RESOLVE':
        return 'alert-success';    // GREEN for Resolved
      default:
        return 'alert-info';       // BLUE for others
    }
  }

  // Add this helper method to extract status from notification
  getNotificationStatus(notification: Notification): string {
    // Method 1: If status is in notification content
    const content = notification.content.toLowerCase();
    
    if (content.includes('status: active') || content.includes('active')) {
      return 'ACTIVE';
    }
    if (content.includes('status: pending') || content.includes('pending')) {
      return 'PENDING';
    }
    if (content.includes('status: resolved') || content.includes('resolved') || content.includes('resolve')) {
      return 'RESOLVED';
    }
    
    // Method 2: If you have status as a separate property
    const extendedNotification = notification as any;
    if (extendedNotification.status) {
      return extendedNotification.status;
    }
    
    // Method 3: Based on notification type (fallback)
    switch (notification.type) {
      case 'danger':
        return 'ACTIVE';
      case 'warning':
        return 'PENDING';
      case 'success':
        return 'RESOLVED';
      default:
        return 'INFO';
    }
  }

  // Get display content with proper HTML formatting
  getDisplayContent(content: string): string {
    return content.replace(/\n/g, '<br>');
  }

  // Bulk actions for notifications
  selectAllNotifications(): void {
  }

  // Export notifications (optional feature)
  exportNotifications(): void {
    const notificationData = this.notifications.map(n => ({
      id: n.id,
      type: n.type,
      content: n.content,
      timestamp: n.timestamp
    }));
    
  }

  // // Filter notifications by date range
  // filterNotificationsByDate(startDate: Date, endDate: Date): Notification[] {
  //   return this.notifications.filter(n => 
  //     n.timestamp >= startDate && n.timestamp <= endDate
  //   );
  // }

  // // Search notifications
  // searchNotifications(query: string): Notification[] {
  //   const lowercaseQuery = query.toLowerCase();
  //   return this.notifications.filter(n =>
  //     n.content.toLowerCase().includes(lowercaseQuery)
  //   );
  // }

  // Archive old notifications (older than 7 days)
  // archiveOldNotifications(): void {
  //   const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
  //   const oldNotifications = this.notifications.filter(n => n.timestamp < sevenDaysAgo);
    
  //   oldNotifications.forEach(notification => {
  //     this.notificationService.deleteNotification(notification.id);
  //   });
    
  // }

  // // Batch operations
  // performBatchAction(action: 'delete' | 'archive' | 'read', notificationIds: string[]): void {
  //   switch (action) {
  //     case 'delete':
  //       notificationIds.forEach(id => this.notificationService.deleteNotification(id));
  //       break;
  //     case 'archive':
  //       // Implement archive logic
  //       break;
  //     case 'read':
  //       // Mark as read logic
  //       break;
  //   }
  // }

  // Priority sorting
  sortNotificationsByPriority(): Notification[] {
    const priorityOrder = { 'danger': 0, 'warning': 1, 'info': 2, 'success': 3 };
    return [...this.notifications].sort((a, b) => {
      const aPriority = priorityOrder[a.type] ?? 4;
      const bPriority = priorityOrder[b.type] ?? 4;
      return aPriority - bPriority;
    });
  }

  // Get notification statistics
  getNotificationStats(): { total: number; byType: Record<string, number> } {
    const stats = {
      total: this.notifications.length,
      byType: {} as Record<string, number>
    };
    
    this.notifications.forEach(notification => {
      stats.byType[notification.type] = (stats.byType[notification.type] || 0) + 1;
    });
    
    return stats;
  }

  // Handle keyboard shortcuts
  handleKeyboardShortcut(event: KeyboardEvent): void {
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'a':
          event.preventDefault();
          this.selectAllNotifications();
          break;
        case 'r':
          event.preventDefault();
          this.refreshFromApi();
          break;
        case 'c':
          event.preventDefault();
          this.clearAllNotifications();
          break;
      }
    }
  }

  // Add these lifecycle hooks if not already present
  ngAfterViewInit(): void {
  }

  // Handle component focus/blur
  onFocus(): void {
  }

  onBlur(): void {
    if (this.showNotifications) {
      this.closeNotifications();
    }
  }

  // Handle window resize
  onWindowResize(): void {
    // Close dropdown on mobile when window resizes
    if (window.innerWidth < 768 && this.showNotifications) {
      this.closeNotifications();
    }
  }

  // Accessibility: announce notifications to screen readers
  announceNotification(notification: Notification): void {
    const announcement = `New ${notification.type} notification: ${this.getAlertTitle(notification)}`;
  }
  sortNotificationsByCreated(notifications: any[]): any[] {
  return notifications.sort((a, b) => {
    const dateA = new Date(a.Created);
    const dateB = new Date(b.Created);
    return dateB.getTime() - dateA.getTime(); // descending
  });
}

  private sortNotificationsByCreatedDate(notifications: Notification[]): Notification[] {
  return notifications.sort((a, b) => {
    // Extract created date from notification content
    const createdA = this.extractCreatedDate(a);
    const createdB = this.extractCreatedDate(b);
    
    // Sort in descending order (newest first)
    return createdB.getTime() - createdA.getTime();
  });
}

// Add helper method to extract created date
private extractCreatedDate(notification: Notification): Date {
  // First, try to use the notification's timestamp
  if (notification.createdAt) {
    return new Date(notification.createdAt);
  }
  
  // Then try to parse created date from content
  const info = this.parseNotificationContent(notification.content);
  if (info.Created) {
    const createdDate = new Date(info.Created);
    if (!isNaN(createdDate.getTime())) {
      return createdDate;
    }
  }
  
  // Fallback to current time if no valid date found
  return new Date();
}
  goLiveIncident(item: any) {
    // Extract incident ID from notification (same logic as onNotificationClick)
    const notification = this.notifications.find(n => n.id === item.id) || this.latestNotification || item;
    if (!notification) return;
    
    // Try to extract incident ID from notification ID
    let incidentId = this.extractIncidentId(notification.id);
    
    // Fallback: find incident from content
    if (!incidentId) {
      incidentId = this.findIncidentIdFromContent(notification);
    }
    
    this.showPinDialog = true;
    this.ModalId = incidentId; // Store the actual incident ID, not notification ID
  }
  onPinDialogClosed(result: any) {
    this.pinValue = '';
    this.showPinDialog = false;
  }
   confirmPublicIncident(entity: any) {
    this.settingService.verifyPin(this.pinValue).subscribe(
      (response) => {
        if (!response.validPin) {
          this.showAlert('Invalid PIN');
          return;
        }
        
        // Use the incident ID stored in ModalId (set by goLiveIncident)
        const incidentId = this.ModalId;
        if (!incidentId) {
          this.showAlert('Invalid incident ID');
          return;
        }
        
       const orgId = this.arcAngelMonitorModeService.getOrgIdForLiveIncident(incidentId);
       //this.illumLiveService.setManagingOrgId(orgId ?? '0');
       this.incidentCategoryService.setManagingOrgId(orgId ?? '0');
        this.sessionOptionsService.goToLiveIncidentMode(
          incidentId,
          true,
          true
        );
       const eventId = incidentId;
       this.arcAngelLiveModeService.startLiveIncidentMode(eventId, this);
       //this.openPinDialog.emit(eventId);
       this.onPinDialogClosed('false');
        const geometries = this.geofenceResults?.rows
          .filter((item: any) => item.incidentId === incidentId)
          .map((item: any) => item.geometry);
        if (geometries?.length > 0) {
          const [longitude, latitude] = geometries[0].coordinates[0][0];
          this.showBuildingCamera = true;
          this.onPinDialogClosed('false');
          this.camera.flyToLocation(latitude, longitude, 500);
          return;
        }
      },
      () => {
        return this.showAlert('Invalid PIN');
      }
    );
  }
    private showAlert(message: string) {
      const modalRef = this.modalService.open(ResponseModalComponent, {
        centered: true,
        windowClass: 'response-modal-top zindex',
        backdropClass: 'response-modal-backdrop-top',
      });
      modalRef.componentInstance.message = message;
      modalRef.componentInstance.showCancel = false;
      return modalRef.result;
    }

}